import { FeaturePageLayout } from '../../components/features/FeaturePageLayout';
import { ReportingMockup, KpiMockup } from '../../components/features/ProductMockups';

const RELATED = [
  { label: 'Tableau de bord', href: '/fonctionnalites/tableau-bord-temps-reel' },
  { label: 'Prévision de trésorerie', href: '/fonctionnalites/prevision-tresorerie' },
  { label: 'Alertes', href: '/fonctionnalites/alertes-tresorerie' },
  { label: 'Stress test', href: '/fonctionnalites/stress-test-tresorerie' },
];

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function ReportingFinancierPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return (
    <FeaturePageLayout
      seo={{
        title: 'Reporting financier automatique — Rapports mensuels PDF | TrezoOne',
        description: 'Générez automatiquement vos rapports financiers mensuels : KPI, graphiques, top charges, export PDF. Conçu pour les associations, PME et dirigeants sans expertise comptable.',
        canonical: '/fonctionnalites/reporting-financier',
        keywords: 'reporting financier automatique, rapport mensuel association, export PDF trésorerie, indicateurs financiers KPI',
      }}
      activeNavId="reporting"
      hero={{
        tag: 'Reporting financier',
        title: 'Vos rapports financiers mensuels, générés automatiquement.',
        subtitle: 'TrezoOne compile vos données et génère un rapport complet chaque mois : KPI clés, graphiques d\'évolution, top charges, analyse comparative et score de pilotage. Exportable en PDF en un clic.',
        mockup: <ReportingMockup />,
      }}
      problem={{
        title: 'Préparer un rapport financier mensuel vous prend trop de temps',
        points: [
          'Vous passez 2 à 3 heures chaque mois à consolider vos données dans Excel pour produire un reporting.',
          'Vos rapports ne sont pas homogènes d\'un mois à l\'autre, ce qui complique le suivi dans le temps.',
          'Vous n\'avez pas de score ou d\'indicateur synthétique pour évaluer rapidement la santé financière.',
          'Partager des rapports avec le CA ou les financeurs est laborieux et peu professionnel.',
        ],
      }}
      steps={[
        { num: 1, title: 'Importez vos transactions du mois', desc: 'Téléchargez votre relevé bancaire mensuel. TrezoOne catégorise et intègre les opérations automatiquement.' },
        { num: 2, title: 'Le rapport est généré', desc: 'TrezoOne calcule automatiquement tous les indicateurs : recettes, dépenses, résultat, trésorerie, score de pilotage.' },
        { num: 3, title: 'Consultez et personnalisez', desc: 'Visualisez votre rapport dans l\'interface. Ajoutez des annotations ou commentaires si nécessaire.' },
        { num: 4, title: 'Exportez en PDF', desc: 'Téléchargez votre rapport en PDF mis en page et prêt à partager avec votre CA, vos financeurs ou votre équipe.' },
      ]}
      screens={[<ReportingMockup />, <KpiMockup />]}
      useCases={[
        { audience: 'Associations', benefit: 'Présentez un rapport mensuel structuré à votre conseil d\'administration sans effort de mise en forme.' },
        { audience: 'PME', benefit: 'Suivez vos performances mensuelles avec des KPI clairs et une lecture guidée.' },
        { audience: 'Trésorier(e)', benefit: 'Automatisez la production du reporting mensuel et consacrez votre temps à l\'analyse.' },
        { audience: 'Dirigeant', benefit: 'Recevez un rapport synthétique lisible, même sans expertise comptable.' },
        { audience: 'Financeurs', benefit: 'Fournissez des rapports intermédiaires structurés pour vos bailleurs et partenaires.' },
        { audience: 'Comptable', benefit: 'Complétez votre vision comptable avec un reporting opérationnel mensuel immédiat.' },
      ]}
      benefits={[
        'Réduire le temps de préparation du reporting de 2h à quelques minutes',
        'Avoir des rapports homogènes et comparables mois après mois',
        'Présenter des données professionnelles à vos financeurs et partenaires',
        'Identifier rapidement les dérives par rapport aux objectifs',
        'Conserver un historique complet de vos rapports',
        'Partager facilement par email ou en réunion de CA',
      ]}
      faq={[
        { q: 'Le rapport mensuel est-il généré automatiquement ?', a: 'Oui. Dès que vous importez vos transactions, TrezoOne génère un rapport mensuel complet avec tous les indicateurs calculés.' },
        { q: 'Qu\'est-ce que le score de pilotage ?', a: 'Le score de pilotage est une note de 0 à 100 qui évalue la santé financière globale de votre structure, basée sur plusieurs critères : régularité du suivi, niveau de trésorerie, respect du budget, tendance des résultats.' },
        { q: 'Le PDF est-il mis en page professionnellement ?', a: 'Oui. Le rapport PDF est mis en page avec votre nom d\'organisation, le logo TrezoOne, les graphiques et les tableaux. Il est directement partageable.' },
        { q: 'Peut-on conserver l\'historique des rapports ?', a: 'Oui. TrezoOne conserve l\'ensemble de vos rapports mensuels avec la possibilité de les consulter et télécharger à tout moment.' },
      ]}
      relatedLinks={RELATED}
      onNavigate={onNavigate}
      onSignIn={onSignIn}
      onSignUp={onSignUp}
    />
  );
}
